import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLatest } from 'react-use';

import { ValidatorComponentProps } from '@/features/csv-upload';
import { useSettingValues } from '@/features/settings/hooks/use-setting-values';
import { SettingIdValue } from '@/types/api';

import { CreateItemTemplateDto } from '../api';
import {
  ItemTemplateCsvParseResult,
  parseItemTemplateCsvResult,
} from '../utils/item-template-csv-parser';

import { ItemTemplateCsvRowPreview, TemplateRoleState } from './item-template-csv-row-preview';

export interface ItemTemplateCsvValidatorState {
  result: ItemTemplateCsvParseResult | null;
  roleStates: TemplateRoleState[];
}

export const ItemTemplateCsvValidator = ({
  csvParseResult,
  onValidData,
  onInvalidData,
  onError,
  state,
  setState,
}: ValidatorComponentProps<CreateItemTemplateDto, ItemTemplateCsvValidatorState>) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<ItemTemplateCsvParseResult | null>(state?.result ?? null);
  const [isLoading, setIsLoading] = useState(false);
  const [roleStates, setRoleStates] = useState<TemplateRoleState[]>(state?.roleStates ?? []);

  const onInvalidDataRef = useLatest(onInvalidData);
  const onErrorRef = useLatest(onError);
  const onValidDataRef = useLatest(onValidData);
  const setStateRef = useLatest(setState);
  const { getSettingValue, isLoading: isLoadingSettings } = useSettingValues();

  useEffect(() => {
    setStateRef.current({
      result: result ?? null,
      roleStates: roleStates,
    });
  }, [result, roleStates, setStateRef]);

  useEffect(() => {
    if (!result || roleStates.length > 0) return;
    // Initialize role states for templates with conflicts
    const initialRoleStates: TemplateRoleState[] = result.data.map((template, index) => ({
      templateIndex: index,
      resolvedRoleTags: template.projectRoleTags ?? [],
      conflictResolutions: [],
      removedConflicts: [],
      additionalRoleTags: [],
    }));
    setRoleStates(initialRoleStates);
  }, [result, roleStates.length]);

  const handleRoleStateChange = (templateIndex: number, newState: TemplateRoleState) => {
    setRoleStates((prev) => {
      const existing = prev.find((state) => state.templateIndex === templateIndex);
      if (existing) {
        return prev.map((state) => (state.templateIndex === templateIndex ? newState : state));
      } else {
        return [...prev, newState];
      }
    });
  };

  useEffect(() => {
    if (!csvParseResult) return;
    if (isLoading || isLoadingSettings) return;
    setIsLoading(true);
    parseItemTemplateCsvResult(
      csvParseResult,
      {
        constructionTypes: getSettingValue<SettingIdValue<string>[]>('constructionTypes', []),
        itemTypes: getSettingValue<SettingIdValue<string>[]>('itemTypes', []),
        quotationTypes: getSettingValue<SettingIdValue<string>[]>('quotationTypes', []),
        locationTypes: getSettingValue<SettingIdValue<string>[]>('locationTypes', []),
      },
      t,
    )
      .then((result) => {
        setResult(result);
      })
      .catch((error: unknown) => {
        setResult(null);
        console.error(error);
        onErrorRef.current(String(error));
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csvParseResult]);

  // Check if all role conflicts are resolved
  const allConflictsResolved = useMemo(() => {
    if (!result?.roleConflicts.length) return true;

    return result.roleConflicts.every((templateConflict) => {
      const templateState = roleStates.find(
        (state) => state.templateIndex === templateConflict.templateIndex,
      );
      if (!templateState) return false;

      // Check if all conflicts are either resolved or removed
      return templateConflict.conflicts.every((conflict) => {
        const isRemoved = templateState.removedConflicts.includes(conflict.csvRoleName);
        const isResolved = templateState.conflictResolutions.some(
          (resolution) =>
            resolution.csvRoleName === conflict.csvRoleName && resolution.selectedRoleTag,
        );
        return isRemoved || isResolved;
      });
    });
  }, [result?.roleConflicts, roleStates]);

  // Generate final templates with resolved roles
  const finalTemplates = useMemo(() => {
    if (!result?.data.length) return [];

    return result.data.map((template, index) => {
      const templateState = roleStates.find((state) => state.templateIndex === index);

      if (!templateState) {
        // No state changes, return original template
        return template;
      }

      // Combine all role tags: resolved + conflict resolutions + additional
      const conflictRoleTags = templateState.conflictResolutions
        .filter((cr) => cr.selectedRoleTag)
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .map((cr) => cr.selectedRoleTag!);

      const finalRoleTags = [
        ...templateState.resolvedRoleTags,
        ...conflictRoleTags,
        ...templateState.additionalRoleTags,
      ];

      return {
        ...template,
        projectRoleTags: finalRoleTags,
      };
    });
  }, [result?.data, roleStates]);

  useEffect(() => {
    if (allConflictsResolved) {
      onValidDataRef.current(finalTemplates as CreateItemTemplateDto[]);
    } else {
      onInvalidDataRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- using useLatest for the refs
  }, [allConflictsResolved, finalTemplates]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!result) {
    return null;
  }

  return (
    <Box>
      {result.data.length > 0 ? (
        <Alert severity={allConflictsResolved ? 'success' : 'warning'}>
          <Typography variant="body2">
            {allConflictsResolved ? (
              <>{t('csvImport.parseSuccess', { count: result.data.length })}</>
            ) : (
              <>{t('csvImport.resolveConflicts')}</>
            )}
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning">
          <Typography variant="body2">{t('csvImport.noValidTemplates')}</Typography>
        </Alert>
      )}

      {(result.data.length > 0 || result.errors.length > 0) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('csvImport.preview')}
          </Typography>
          <ItemTemplateCsvRowPreview
            templates={result.data}
            roleConflicts={result.roleConflicts}
            roleStates={roleStates}
            onRoleStateChange={handleRoleStateChange}
            errors={result.errors}
          />
        </Box>
      )}
    </Box>
  );
};
